import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Clock, Trophy, MapPin, Award } from 'lucide-react';
import { Player } from '../../types';
import type { UnifiedMatch } from './UnifiedHistory';
import { PairAvatar } from './PairAvatar';

interface Props {
  match: UnifiedMatch;
  players: Player[];
  onClose: () => void;
}

export function HistoryMatchDetailModal({ match, players, onClose }: Props) {
  // Try to extract time from originalMatch if possible
  let timeStr = 'N/A';
  if (match.source === 'matchmaking' && 'time' in match.originalMatch) {
    timeStr = match.originalMatch.time;
  } else if (match.timestamp) {
    const d = new Date(match.timestamp);
    timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  // Location logic
  const locationStr = match.locationName || 'Ciudad';

  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 sm:p-6" style={{ isolation: 'isolate' }}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-md"
          onClick={onClose}
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', bounce: 0.3 }}
          className="relative w-full max-w-lg bg-zinc-900 border border-white/10 rounded-[2rem] shadow-2xl flex flex-col overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative p-6 border-b border-white/5 bg-transparent">
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10 text-white/50 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
            <div className="flex flex-col items-center justify-center text-center gap-2">
              <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center shadow-inner mb-2">
                <Trophy className={match.source === 'tournament' ? 'text-yellow-400' : 'text-lime-400'} size={24} />
              </div>
              <h2 className="text-xl font-black uppercase tracking-widest text-white">Detalle del Partido</h2>
              <div className="flex flex-wrap items-center justify-center gap-2 text-xs font-bold uppercase tracking-wider">
                <span className={match.source === 'tournament' ? 'text-yellow-400/80' : 'text-lime-400/80'}>
                  {match.matchTypeStr}
                </span>
                <span className="text-white/20">•</span>
                <span className="text-white/50 text-center">{match.tournamentName}</span>
              </div>
              {match.phaseStr && (
                <div className="mt-2 text-[10px] font-black text-white/50 uppercase tracking-widest bg-white/5 px-3 py-1 rounded-full border border-white/10">
                  {match.phaseStr}
                </div>
              )}
            </div>
          </div>

          {/* Details Bar */}
          <div className="flex items-center justify-between px-6 py-4 bg-black/20 border-b border-white/5">
            <div className="flex items-center gap-2 text-white/60">
              <Calendar size={14} className="opacity-50" />
              <span className="text-[10px] font-black uppercase tracking-widest">{match.dateStr}</span>
            </div>
            <div className="flex items-center gap-2 text-white/60">
              <Clock size={14} className="opacity-50" />
              <span className="text-[10px] font-black uppercase tracking-widest">{timeStr}</span>
            </div>
            <div className="flex items-center gap-2 text-white/60">
              <MapPin size={14} className="opacity-50" />
              <span className="text-[10px] font-black uppercase tracking-widest truncate max-w-[100px]">{locationStr}</span>
            </div>
          </div>

          {/* Versus Area */}
          <div className="p-6 sm:p-8 flex flex-col gap-8 bg-black/20">
            {/* Team 1 */}
            <div className={`relative flex flex-col items-center p-4 rounded-2xl border ${match.team1Won ? 'bg-white/5 border-lime-500/30 shadow-[0_0_30px_rgba(132,204,22,0.15)]' : 'bg-transparent border-white/5'}`}>
              {match.team1Won && (
                <div className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-lime-500 flex items-center justify-center shadow-lg">
                  <Award size={16} className="text-black" />
                </div>
              )}
              <PairAvatar 
                p1Id={match.team1.p1Id} 
                p2Id={match.team1.p2Id}
                p1Image={players.find(p => p.id === match.team1.p1Id)?.photoUrl}
                p2Image={players.find(p => p.id === match.team1.p2Id)?.photoUrl}
                className="w-16 h-16 mb-3" 
              />
              <h3 className={`text-sm sm:text-base font-black uppercase tracking-widest text-center ${match.team1Won ? 'text-lime-400' : 'text-white'}`}>
                {match.team1.name}
              </h3>
              {match.team1Won && <span className="text-[10px] font-bold text-lime-500/70 uppercase tracking-widest mt-1">Ganador</span>}
            </div>

            {/* VS Badge & Score */}
            <div className="flex flex-col items-center justify-center gap-3 relative z-10 -my-4">
              <div className="px-6 py-2 rounded-xl bg-black border border-white/10 shadow-2xl flex items-center justify-center">
                <span className="text-xl font-black theme-accent-text font-mono tracking-[0.2em]">{match.scoreStr || 'Sin Resultado'}</span>
              </div>
            </div>

            {/* Team 2 */}
            <div className={`relative flex flex-col items-center p-4 rounded-2xl border ${match.team2Won ? 'bg-white/5 border-lime-500/30 shadow-[0_0_30px_rgba(132,204,22,0.15)]' : 'bg-transparent border-white/5'}`}>
              {match.team2Won && (
                <div className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-lime-500 flex items-center justify-center shadow-lg">
                  <Award size={16} className="text-black" />
                </div>
              )}
              <PairAvatar 
                p1Id={match.team2.p1Id} 
                p2Id={match.team2.p2Id}
                p1Image={players.find(p => p.id === match.team2.p1Id)?.photoUrl}
                p2Image={players.find(p => p.id === match.team2.p2Id)?.photoUrl}
                className="w-16 h-16 mb-3" 
              />
              <h3 className={`text-sm sm:text-base font-black uppercase tracking-widest text-center ${match.team2Won ? 'text-lime-400' : 'text-white'}`}>
                {match.team2.name}
              </h3>
              {match.team2Won && <span className="text-[10px] font-bold text-lime-500/70 uppercase tracking-widest mt-1">Ganador</span>}
            </div>
          </div>

        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
}
